import { keepPreviousData } from '@tanstack/react-query';
import {
  type ColumnDef,
  type PaginationState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Eye, MoreHorizontal, Printer, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';

import {
  type ListPrintingRequests,
  listPrintingRequestsSchema,
} from '@virtual-care/client';
import { displayName, formatDateTimeShort } from '@virtual-care/isomorphic';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from '@virtual-care/shadcn';
import {
  RESERVATION_STATUS_META,
  ReservationStatus,
  SearchInput,
  useData,
} from '@virtual-care/ui';

import { CountablePageHeader } from '../../components/page-header.tsx';
import { Pagination } from '../../components/pagination.tsx';
import { AssignPrintingDoctorDialog } from './assign-printing-doctor-dialog.tsx';

type PrintingRequestRecord = ListPrintingRequests['records'][number];

const statusLabel = (status: string) =>
  RESERVATION_STATUS_META[status as keyof typeof RESERVATION_STATUS_META]?.en ??
  status;

const StatusEnum = listPrintingRequestsSchema.shape.status;

export default function PrintingRequestsPage() {
  const [searchQuery, setSearchQuery] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<
    z.infer<typeof StatusEnum> | undefined
  >();
  const [selected, setSelected] = useState<PrintingRequestRecord | null>(null);
  const [showAssignDoctor, setShowAssignDoctor] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const dataQuery = useData(
    'GET /printing-requests',
    {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      status: statusFilter,
      query: searchQuery || undefined,
    },
    { placeholderData: keepPreviousData },
  );

  const columns: ColumnDef<PrintingRequestRecord>[] = [
    {
      accessorKey: 'counter',
      header: 'Case #',
      cell: ({ row }) => (
        <div className="font-mono font-medium">#{row.getValue('counter')}</div>
      ),
    },
    {
      id: 'patientName',
      header: 'Patient',
      cell: ({ row }) => (
        <div className="text-sm font-medium">
          {displayName(row.original.patient)}
        </div>
      ),
    },
    {
      id: 'doctorName',
      header: 'Assigned Doctor',
      cell: ({ row }) => {
        const doctor = row.original.doctor;
        return doctor ? (
          <div className="text-sm">
            {doctor.firstName} {doctor.lastName}
          </div>
        ) : (
          <span className="text-muted-foreground text-sm">Not assigned</span>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const record = row.original;
        return (
          <ReservationStatus.Root status={record.status} />
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }) => (
        <div className="text-sm">
          {formatDateTimeShort(row.original.createdAt)}
        </div>
      ),
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const record = row.original;
        const canAssign =
          !record.doctorId &&
          record.status !== 'FINISHED';

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Case Actions</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => {
                  setSelected(record);
                  setShowDetails(true);
                }}
              >
                <Eye className="mr-2 h-4 w-4" />
                View details
              </DropdownMenuItem>
              {canAssign && (
                <DropdownMenuItem
                  onClick={() => {
                    setSelected(record);
                    setShowAssignDoctor(true);
                  }}
                >
                  <UserPlus className="mr-2 h-4 w-4" />
                  Assign doctor
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const table = useReactTable({
    data: dataQuery.data?.records ?? [],
    columns,
    manualPagination: true,
    rowCount: dataQuery.data?.totalCount,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange: setPagination,
    state: { pagination },
  });

  return (
    <div className="w-full">
      <CountablePageHeader
        title="3D Printing Requests"
        totalCount={`${dataQuery.data?.totalCount ?? 0} total requests`}
        icon={Printer}
      />

      <div className="mb-4 flex items-center gap-4">
        <div className="flex-1">
          <SearchInput
            placeholder="Search by patient name, case number, or description..."
            value={searchQuery}
            onChange={setSearchQuery}
            onClear={() => setSearchQuery('')}
            isLoading={dataQuery.isFetching}
          />
        </div>
        <Select
          value={statusFilter ?? 'all'}
          onValueChange={(v) =>
            setStatusFilter(v === 'all' ? undefined : StatusEnum.parse(v))
          }
        >
          <SelectTrigger className="min-h-11">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="WAITING_DOCTOR_ASSIGNMENT">
              {statusLabel('WAITING_DOCTOR_ASSIGNMENT')}
            </SelectItem>
            <SelectItem value="WAITING_DOCTOR_CONFIRMATION">
              {statusLabel('WAITING_DOCTOR_CONFIRMATION')}
            </SelectItem>
            <SelectItem value="CONFIRMED">
              {statusLabel('CONFIRMED')}
            </SelectItem>
            <SelectItem value="FINISHED">{statusLabel('FINISHED')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {dataQuery.isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No printing requests found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Pagination
        currentPage={table.getState().pagination.pageIndex}
        pageSize={table.getState().pagination.pageSize}
        pageCount={table.getPageCount()}
        totalCount={table.getRowCount()}
        onPageChange={table.setPageIndex}
        onPageSizeChange={table.setPageSize}
      />

      {selected && (
        <>
          <AssignPrintingDoctorDialog
            selectedRequest={selected}
            open={showAssignDoctor}
            onOpenChange={setShowAssignDoctor}
            onSuccess={() => void dataQuery.refetch()}
          />
          <PrintingRequestDetailsDialog
            record={selected}
            open={showDetails}
            onOpenChange={setShowDetails}
          />
        </>
      )}
    </div>
  );
}

function PrintingRequestDetailsDialog({
  record,
  open,
  onOpenChange,
}: {
  record: PrintingRequestRecord;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Printing Request #{record.counter}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p>
            <span className="text-muted-foreground">Patient:</span>{' '}
            <span className="font-medium">{displayName(record.patient)}</span>
          </p>
          <p>
            <span className="text-muted-foreground">Status:</span>{' '}
            <ReservationStatus.Root status={record.status} />
          </p>
          <p>
            <span className="text-muted-foreground">Description:</span>
          </p>
          <p className="bg-muted/50 rounded p-3 whitespace-pre-wrap">
            {record.description}
          </p>
          {record.downloadUrl && (
            <p>
              <span className="text-muted-foreground">Download URL:</span>{' '}
              <a
                href={record.downloadUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="font-mono text-xs break-all underline"
              >
                {record.downloadUrl}
              </a>
            </p>
          )}
          {record.files.length > 0 && (
            <div>
              <p className="text-muted-foreground mb-1">Files:</p>
              <ul className="space-y-1">
                {record.files.map((f) => (
                  <li key={f.id} className="font-mono text-xs">
                    {f.file.name}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
